/**
 * Statistical computations on exact Rational arrays.
 * All results are exact — no floating point.
 */
import { Rational, rat } from "./rational";

/** Sort rationals ascending. Returns a new array. */
export function sortAscending(values: readonly Rational[]): Rational[] {
  return [...values].sort((a, b) => a.compare(b));
}

/** Exact mean of a non-empty set. */
export function mean(values: readonly Rational[]): Rational {
  if (values.length === 0) throw new RangeError("mean of empty set");
  let sum = Rational.ZERO;
  for (const v of values) sum = sum.add(v);
  return sum.div(rat(values.length));
}

/** Exact median of a non-empty sorted set. */
export function median(sorted: readonly Rational[]): Rational {
  if (sorted.length === 0) throw new RangeError("median of empty set");
  const mid = Math.floor(sorted.length / 2);
  if (sorted.length % 2 === 1) return sorted[mid] as Rational;
  return (sorted[mid - 1] as Rational).add(sorted[mid] as Rational).div(rat(2));
}

/** Mode(s) of a set. Returns all values with the highest frequency, or [] if no repeats. */
export function mode(values: readonly Rational[]): Rational[] {
  const freq = new Map<string, { value: Rational; count: number }>();
  for (const v of values) {
    const key = v.key();
    const entry = freq.get(key);
    if (entry) {
      entry.count++;
    } else {
      freq.set(key, { value: v, count: 1 });
    }
  }
  let maxCount = 0;
  for (const entry of freq.values()) {
    if (entry.count > maxCount) maxCount = entry.count;
  }
  if (maxCount <= 1) return [];
  const modes: Rational[] = [];
  for (const entry of freq.values()) {
    if (entry.count === maxCount) modes.push(entry.value);
  }
  return sortAscending(modes);
}

/** Range = max - min. */
export function range(sorted: readonly Rational[]): Rational {
  if (sorted.length === 0) throw new RangeError("range of empty set");
  return (sorted[sorted.length - 1] as Rational).sub(sorted[0] as Rational);
}

/** Five-number summary for box plots. */
export interface FiveNumberSummary {
  readonly min: Rational;
  readonly q1: Rational;
  readonly median: Rational;
  readonly q3: Rational;
  readonly max: Rational;
}

/**
 * Compute five-number summary. SC standard: median is excluded when
 * calculating Q1 and Q3.
 */
export function fiveNumberSummary(sorted: readonly Rational[]): FiveNumberSummary {
  if (sorted.length < 5) throw new RangeError("need at least 5 data points for box plot");
  const med = median(sorted);
  const mid = Math.floor(sorted.length / 2);
  const lower = sorted.slice(0, mid);
  const upper = sorted.length % 2 === 0 ? sorted.slice(mid) : sorted.slice(mid + 1);
  return {
    min: sorted[0] as Rational,
    q1: median(lower),
    median: med,
    q3: median(upper),
    max: sorted[sorted.length - 1] as Rational,
  };
}

/** Interquartile range = Q3 - Q1. */
export function iqr(summary: FiveNumberSummary): Rational {
  return summary.q3.sub(summary.q1);
}

/** Mean Absolute Deviation. */
export function mad(values: readonly Rational[]): Rational {
  if (values.length === 0) throw new RangeError("mad of empty set");
  const m = mean(values);
  let sum = Rational.ZERO;
  for (const v of values) sum = sum.add(v.sub(m).abs());
  return sum.div(rat(values.length));
}
